import { z } from 'zod';

import { ZodAmount, ZodCurrency } from '@Common/types/Amount';
import { BoListQueryBase } from '@Common/types/bo/bo_list_query';
import { UBO_EDIT_FIELDS } from '@Common/types/bo/ubo_edit_fields';
import { buildZodBoListQueryParsed } from '@Common/types/bo/utils/build_zod_bo_list_query';
import { zodDecimalAmount5, zodIntegerAmount2 } from '@Common/types/currency/currency';
import { ZodIDPathParam, ZodId } from '@Common/types/request';
import {
  ZodBankAccountBic,
  ZodBankAccountIban,
  ZodIntlAccountNumber,
  ZodNonSepaIntlBic,
  ZodUkAccountNumber,
  ZodUkSortCode,
} from '@Common/types/zod/bank_account';
import { type CoreZodSchema } from '@Common/types/zod/utils';
import { ZodDateTimeString } from '@Common/types/zod/zod-date-string';
import { endpoint, GetEndpointType } from '@Common/types/zod/zod-endpoint';
import { ZodTrimmedString } from '@Common/types/zod/zod-trimmed-string';

const ZodRestrictionsFlag = z.enum([
  'suspended',
  'block_money_actions',
  'block_deposit_actions',
  'block_withdrawal_actions',
  'closing',
  'closed',
  'social_ban',
]); // TODO sync with mobile part once https://sh4res.atlassian.net/browse/CORE-6003 this is done

export const ReviewedRequestTypeValues = [
  'user_edit',
  'cash_granting',
  'user_action',
  'unlink_payment',
  'add_shares_ca_announcement',
  'create_withdrawal',
  'create_internal_account_withdrawal',
  'create_alpaca_account_withdrawal',
  'pea_transfer_out',
  'create_beneficiary',
  'instant_oddo_submission',
  'cancel_aggregated_od_order',
  'sell_instrument',
  'change_wealth_manager',
] as const;
export type ReviewedRequestType = (typeof ReviewedRequestTypeValues)[number];

export const ReviewedRequestStateValues = [
  'REQUESTED',
  'APPROVED',
  'REJECTED',
  'APPROVED_WITH_ERROR',
  'CANCELLED',
] as const;
export const ZodReviewedRequestState = z.enum(ReviewedRequestStateValues);
export type ReviewedRequestState = z.infer<typeof ZodReviewedRequestState>;

export const ZodReviewedRequestAction = z.enum(['APPROVED', 'REJECTED']);
export type ReviewedRequestAction = z.infer<typeof ZodReviewedRequestAction>;

export const additionalClosingOperationValues = [
  'SELL_ALL_POSITIONS',
  'TRANSFER_FUNDS_TO_SHARES',
] as const;

const ZodAdditionalClosingOperation = z.enum(additionalClosingOperationValues);

export type AdditionalClosingOperation = z.infer<typeof ZodAdditionalClosingOperation>;

const ZodUserActionRequestData = z.object({
  restrictions_flags: z.array(ZodRestrictionsFlag).optional(),
  from_restrictions_flags: z.array(ZodRestrictionsFlag).optional(),
  reopen_account: z.boolean().optional(),
  is_ambassador: z.boolean().optional(),
  additional_closing_operation: ZodAdditionalClosingOperation.optional(),
  leave_on_account_after_transfer: z.number().optional(),
  manual_poi: z.boolean().optional(),
  manual_poa: z.boolean().optional(),
  manual_sof: z.boolean().optional(),
});

export type UserActionRequestData = z.infer<typeof ZodUserActionRequestData>;

export const ZodUserEditField = z.enum([
  'firstname',
  'lastname',
  'birthdate',
  'flat_building',
  'street',
  'city',
  'postalcode',
  'mailing_flat_building',
  'mailing_street',
  'mailing_city',
  'mailing_postalcode',
  'country_of_tax_residence',
  'risk_score',
  'phone_number',
  'secure_hash_pin_code',
  'email',
  'profile_pic',
  'is_shares_staff',
  'is_pfnl_dispense_eligible',
  'username',
  'display_name',
  'bio',
  'social_url',
  'sof_yearly_income_limit',
  'sof_savings_limit',
  'invest_range_min',
  'invest_range_max',
  'annual_income_range_min',
  'annual_income_range_max',
  'estimated_net_worth_range_min',
  'estimated_net_worth_range_max',
  'is_politically_exposed',
  'is_politically_exposed_relative',
  'is_control_person',
  'funding_source',
  'employment_status',
  'employer_industry',
  'job_title',
  'vulnerable_comment_choices',
  'linked_users',
  'gender',
  'place_of_birth',
  'postcode_of_birth',
  'nationality',
  'other_nationality',
  'tax_number',
  'reason_of_opening_account',
  'nature_of_transactions',
  'b2b_flags',
  'id_external_taiga',
  'company_name',
  'company_siret',
  'company_siren',
  'company_legal_form',
  'company_registration_date',
  'company_naf_code',
  'company_industry',
  'company_flat_building',
  'company_street',
  'company_city',
  'company_postalcode',
  'company_mailing_flat_building',
  'company_mailing_street',
  'company_mailing_city',
  'company_mailing_postalcode',
  ...UBO_EDIT_FIELDS,
  'wm_fee_rate',
]);

export type UserEditField = z.infer<typeof ZodUserEditField>;

export const ZodUserEditDataOne = z.object({
  field: ZodUserEditField,
  from: ZodTrimmedString,
  to: ZodTrimmedString.nullable(),
  /**
   * Only set for `ubo_*` fields used to identify which UBO of the user company the change applies to
   */
  ubo_id: ZodId.optional(),
  /**
   * Only set for `wm_fee_rate` used to identify which investment account custody fee the change applies to
   */
  investment_account_id: ZodId.optional(),
});
export type UserEditDataOne = z.infer<typeof ZodUserEditDataOne>;

const ZodUserEditData = z.array(ZodUserEditDataOne);

export type UserEditData = z.infer<typeof ZodUserEditData>;

export const ZodCashGrantingRequestData = z.object({
  amount: z.number(),
});
export type CashGrantingRequestData = z.infer<typeof ZodCashGrantingRequestData>;

export const ZodUnlinkPaymentRequestData = z.object({
  bank_account_beneficiary_ids: z.array(ZodId),
  card_ids: z.array(z.number()),
  sepa_dd_mandate_ids: z.array(ZodId).optional().default([]),
});
export type UnlinkPaymentRequestData = z.infer<typeof ZodUnlinkPaymentRequestData>;

export const ZodCreateWithdrawalRequestData = z.object({
  amount: zodIntegerAmount2,
  currency: ZodCurrency,
  user_id: ZodId,
  beneficiary_id: ZodId.optional(),
  card_id: ZodId.optional(),
});
export type CreateWithdrawalRequestData = z.infer<typeof ZodCreateWithdrawalRequestData>;

export const ZodInternalAccountSourceAccountIntent = z.enum([
  'buffer_partner',
  'buffer_oddo',
  'tec_fees',
  'tec_taxes',
  'tec_buffer',
]);
export type InternalAccountSourceAccountIntent = z.infer<
  typeof ZodInternalAccountSourceAccountIntent
>;

// Requests created before the buffers existed still carry coi_external. New requests cannot.
export const ZodStoredInternalAccountSourceAccountIntent = z.enum([
  ...ZodInternalAccountSourceAccountIntent.options,
  'coi_external',
]);
export type StoredInternalAccountSourceAccountIntent = z.infer<
  typeof ZodStoredInternalAccountSourceAccountIntent
>;

export const ZodInternalAccountWithdrawalRequestData = z.object({
  amount: zodIntegerAmount2,
  currency: ZodCurrency,
  beneficiary_id: ZodId,
  source_account_intent: ZodStoredInternalAccountSourceAccountIntent,
});
export type InternalAccountWithdrawalRequestData = z.infer<
  typeof ZodInternalAccountWithdrawalRequestData
>;
export const ZodAlpacaAccountWithdrawalRequestData = z.object({
  amount: zodIntegerAmount2,
  source_account: ZodTrimmedString,
  destination_account: ZodTrimmedString,
});
export type AlpacaAccountWithdrawalRequestData = z.infer<
  typeof ZodAlpacaAccountWithdrawalRequestData
>;

export const ZodPeaTransferOutRequestData = z.object({
  iban: ZodTrimmedString,
  bic: ZodTrimmedString,
  bank_name: ZodTrimmedString,
});
export type PeaTransferOutRequestData = z.infer<typeof ZodPeaTransferOutRequestData>;

const ZodCreateBeneficiaryAddress = z.object({
  street: ZodTrimmedString.nullable().optional(),
  city: ZodTrimmedString.nullable().optional(),
  postcode: ZodTrimmedString.nullable().optional(),
  country: ZodTrimmedString.nullable().optional(),
});

const ZodCreateBeneficiaryAccountBase = z.object({
  first_name: ZodTrimmedString,
  last_name: ZodTrimmedString.nullable().optional(),
  address: ZodCreateBeneficiaryAddress.nullable().optional(),
});

const ZodCreateBeneficiaryAccountIban = z.object({
  ...ZodCreateBeneficiaryAccountBase.shape,
  account_type: z.literal('iban'),
  iban: ZodBankAccountIban,
  bic: ZodBankAccountBic,
});

const ZodCreateBeneficiaryAccountIntl = z.object({
  ...ZodCreateBeneficiaryAccountBase.shape,
  account_type: z.literal('intl'),
  sort_code: ZodTrimmedString.nullable().optional(),
  account_number: ZodIntlAccountNumber,
  bic: ZodNonSepaIntlBic,
});

const ZodCreateBeneficiaryAccountScan = z.object({
  ...ZodCreateBeneficiaryAccountBase.shape,
  account_type: z.literal('scan'),
  sort_code: ZodUkSortCode,
  account_number: ZodUkAccountNumber,
  bic: ZodBankAccountBic,
});

export const ZodCreateBeneficiaryAccount = z.discriminatedUnion('account_type', [
  ZodCreateBeneficiaryAccountIban,
  ZodCreateBeneficiaryAccountIntl,
  ZodCreateBeneficiaryAccountScan,
]);

export const ZodCreateBeneficiaryRibFields = {
  rib_s3_path: ZodTrimmedString.nullable().optional(),
  rib_document_name: ZodTrimmedString.nullable().optional(),
  rib_url: ZodTrimmedString.nullable().optional(),
};

export const ZodCreateBeneficiaryUserOwner = z.object({
  owner_type: z.literal('user'),
  user_id: ZodId,
  b2b_company_id: ZodId.nullable().optional(),
  is_active: z.boolean(),
  account: ZodCreateBeneficiaryAccount,
  ...ZodCreateBeneficiaryRibFields,
});

export const ZodCreateBeneficiaryB2bCompanyOwner = z.object({
  owner_type: z.literal('b2b_company'),
  b2b_company_id: ZodId,
  is_active: z.boolean(),
  account: ZodCreateBeneficiaryAccount,
  ...ZodCreateBeneficiaryRibFields,
});

export const ZodCreateBeneficiaryInternalOwner = z.object({
  owner_type: z.literal('internal'),
  account: ZodCreateBeneficiaryAccount,
  ...ZodCreateBeneficiaryRibFields,
});

export const ZodCreateBeneficiaryRequestData = z.discriminatedUnion('owner_type', [
  ZodCreateBeneficiaryUserOwner,
  ZodCreateBeneficiaryB2bCompanyOwner,
  ZodCreateBeneficiaryInternalOwner,
]);
export type CreateBeneficiaryRequestData = z.infer<typeof ZodCreateBeneficiaryRequestData>;

export const ZodInstantOddoSubmissionRequestData = z.object({
  order_ids: z.array(ZodId).nonempty(),
});
export type InstantOddoSubmissionRequestData = z.infer<typeof ZodInstantOddoSubmissionRequestData>;

export const ZodCancelAggregatedOdOrderData = z.object({
  aggregated_od_order_id: ZodId,
});
export type CancelAggregatedOdOrderData = z.infer<typeof ZodCancelAggregatedOdOrderData>;
export const ZodSellInstrumentRequestData = z.object({
  user_id: ZodId,
  instrument_id: ZodId,
  amount: ZodAmount.optional(),
  quantity: z.number().optional(),
  investment_plan_id: ZodId.optional(),
});

export type SellInstrumentRequestData = z.infer<typeof ZodSellInstrumentRequestData>;

export const ZodChangeWealthManagerRequestData = z.object({
  user_id: ZodId,
  admin_id: ZodId,
  wm_fee_rates: z
    .array(
      z.object({
        investment_account_id: ZodId,
        wm_fee_rate: zodDecimalAmount5,
      }),
    )
    .optional(),
});
export type ChangeWealthManagerRequestData = z.infer<typeof ZodChangeWealthManagerRequestData>;

export const ZodSharesCaAnnouncementsData = z.object({
  type: ZodTrimmedString,
  subtype: ZodTrimmedString.nullable(),
  initiating_instrument_symbol: ZodTrimmedString.nullable(),
  initiating_instrument_id: ZodId.nullable(),
  target_instrument_symbol: ZodTrimmedString.nullable(),
  target_instrument_id: ZodId.nullable(),
  old_rate: z.number().nullable(),
  new_rate: z.number().nullable(),
  cash: z.number().nullable(),
  record_date: ZodDateTimeString.nullable(),
  declaration_date: ZodDateTimeString.nullable(),
  execution_date: ZodDateTimeString.nullable(),
  payable_date: ZodDateTimeString.nullable(),
  reference_nav_date: ZodDateTimeString.nullable().default(null),
  reason: ZodTrimmedString.default(''),
  wht_rate: z.number().nullable().default(null),
  based_on_broker_corporate_action_id: ZodId.nullable().default(null),
  is_special: z.boolean().default(false),
});

export type SharesCaAnnouncementsData = z.infer<typeof ZodSharesCaAnnouncementsData>;

const ZodSpecificReviewedRequest = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('cash_granting'),
    data: ZodCashGrantingRequestData,
  }),
  z.object({
    type: z.literal('user_edit'),
    data: ZodUserEditData,
  }),
  z.object({
    type: z.literal('user_action'),
    data: ZodUserActionRequestData,
  }),
  z.object({
    type: z.literal('unlink_payment'),
    data: ZodUnlinkPaymentRequestData,
  }),
  z.object({
    type: z.literal('add_shares_ca_announcement'),
    data: ZodSharesCaAnnouncementsData,
  }),
  z.object({
    type: z.literal('create_withdrawal'),
    data: ZodCreateWithdrawalRequestData,
  }),
  z.object({
    type: z.literal('create_internal_account_withdrawal'),
    data: ZodInternalAccountWithdrawalRequestData,
  }),
  z.object({
    type: z.literal('create_alpaca_account_withdrawal'),
    data: ZodAlpacaAccountWithdrawalRequestData,
  }),
  z.object({
    type: z.literal('pea_transfer_out'),
    data: ZodPeaTransferOutRequestData,
  }),
  z.object({
    type: z.literal('create_beneficiary'),
    data: ZodCreateBeneficiaryRequestData,
  }),
  z.object({
    type: z.literal('instant_oddo_submission'),
    data: ZodInstantOddoSubmissionRequestData,
  }),
  z.object({ type: z.literal('cancel_aggregated_od_order'), data: ZodCancelAggregatedOdOrderData }),
  z.object({
    type: z.literal('sell_instrument'),
    data: ZodSellInstrumentRequestData,
  }),
  z.object({
    type: z.literal('change_wealth_manager'),
    data: ZodChangeWealthManagerRequestData,
  }),
]);

export type SpecificReviewedRequest = z.infer<typeof ZodSpecificReviewedRequest>;

// A union returns the first matching option and strips unknown keys. `ZodUserActionRequestData`
// has only optional fields, so it matches (and empties) any object and must be tried last.
const isUserAction = (option: (typeof ZodSpecificReviewedRequest.options)[number]) =>
  option.shape.type.value === 'user_action';

export const ZodReviewedRequestData = z.union(
  [
    ...ZodSpecificReviewedRequest.options.filter((option) => !isUserAction(option)),
    ...ZodSpecificReviewedRequest.options.filter(isUserAction),
  ].map((option) => option.shape.data),
);
export type ReviewedRequestData = z.infer<typeof ZodReviewedRequestData>;

export const REVIEWED_REQUEST_DATA_BRAND = 'internal/reviewed_request_data';
export type ReviewedRequestDataBrand = typeof REVIEWED_REQUEST_DATA_BRAND;

type Branded<T> = T & z.$brand<ReviewedRequestDataBrand>;

export type BrandedReviewedRequestsData = Branded<ReviewedRequestData>;

const reviewedRequestDataSchemaByType: Record<string, CoreZodSchema> = Object.fromEntries(
  ZodSpecificReviewedRequest.options.map((option) => [option.shape.type.value, option.shape.data]),
);

type ReviewedRequestDataFor<T extends SpecificReviewedRequest['type']> = Extract<
  SpecificReviewedRequest,
  { type: T }
>['data'];

export function parseReviewedRequestData<T extends SpecificReviewedRequest['type']>(
  type: T,
  data: ReviewedRequestDataFor<T>,
): Branded<ReviewedRequestDataFor<T>> {
  const parsed: unknown = z.parse(reviewedRequestDataSchemaByType[type], data);
  return parsed as Branded<ReviewedRequestDataFor<T>>;
}

/**
 * Used only when type is not known, like a generic update by id
 */
export function parseReviewedRequestsDataUnknownType(
  data: ReviewedRequestData,
): BrandedReviewedRequestsData {
  return ZodReviewedRequestData.parse(data) as BrandedReviewedRequestsData;
}

export const ZodReviewedRequest = z
  .object({
    id: ZodId,
    user_ids: z.array(z.number().int()),
    requester_id: ZodId,
    requester_email: ZodTrimmedString,
    reason: ZodTrimmedString,
    state: ZodReviewedRequestState,
    created_at: ZodDateTimeString,
    updated_at: ZodDateTimeString,
    reviewer_id: ZodId.nullable(),
    reviewer_email: ZodTrimmedString.nullable(),
    reviewed_at: ZodDateTimeString.nullable(),
    error_message: ZodTrimmedString.nullable(),
    mdlr_payment_ids: z.array(ZodTrimmedString),
    assignee_id: ZodId.nullable(),
    assignee_email: ZodTrimmedString.nullable(),
    shares_ca_id: ZodId.nullable(),
  })
  .and(ZodSpecificReviewedRequest);

export type ReviewedRequest = z.infer<typeof ZodReviewedRequest>;

const ZodGetReviewRequestResponseItem = ZodReviewedRequest;

export type GetReviewRequestResponse = z.infer<typeof ZodGetReviewRequestResponseItem>;

const ZodGetReviewRequestsResponse = z.array(ZodReviewedRequest);

export type GetReviewRequestsResponse = z.infer<typeof ZodGetReviewRequestsResponse>;

const ZodPutCancelReviewRequestResponse = z.object({
  id: ZodId,
});

export type PutCancelReviewRequestResponse = {
  id: number;
};

export const reviewedRequestSortColumns: (keyof ReviewedRequest)[] = [
  'id',
  'type',
  'requester_id',
  'requester_email',
  'reason',
  'state',
  'created_at',
  'updated_at',
  'reviewer_id',
  'reviewer_email',
  'reviewed_at',
  'error_message',
  'shares_ca_id',
];

const ZodIntOrIntArray = z.union([z.number().int(), z.array(z.number().int())]);

const ZodStringOrStringArray = z.union([ZodTrimmedString, z.array(ZodTrimmedString)]);

const ZodGetReviewRequestsQueryFilters = z
  .object({
    id: ZodIntOrIntArray.optional(),
    user_ids: ZodIntOrIntArray.optional(),
    requester_id: ZodIntOrIntArray.optional(),
    requester_email: ZodStringOrStringArray.optional(),
    data: ZodStringOrStringArray.optional(),
    state: z.union([ZodReviewedRequestState, z.array(ZodReviewedRequestState)]).optional(),
    reason: ZodStringOrStringArray.optional(),
    from_created_at: ZodStringOrStringArray.optional(),
    to_created_at: ZodStringOrStringArray.optional(),
    type: z
      .union([z.enum(ReviewedRequestTypeValues), z.array(z.enum(ReviewedRequestTypeValues))])
      .optional(),
    reviewer_email: ZodStringOrStringArray.optional(),
    assignee_email: ZodStringOrStringArray.optional(),
    shares_ca_id: ZodId.optional(),
  })
  // Using strict to forbid unknown fields.
  // That should be handled by buildZodBoListQueryParsed.
  // https://sh4res.atlassian.net/browse/TANG-3373
  .strict();

export const ZodGetReviewRequestsQuery = buildZodBoListQueryParsed(
  ZodGetReviewRequestsQueryFilters,
  reviewedRequestSortColumns,
  { range: [0, 100] },
);

export type GetReviewRequestsQuery = BoListQueryBase<
  z.TypeOf<typeof ZodGetReviewRequestsQueryFilters>,
  (typeof reviewedRequestSortColumns)[number]
>;

export const zodAssignBOReviewRequestParams = ZodIDPathParam;

export const zodSelfAssignBOReviewRequestResponse = z.object({
  id: ZodId,
  assignee_id: ZodId,
  assignee_email: ZodTrimmedString,
});

export const selfAssignBOReviewEndpoint = endpoint({
  method: 'PUT',
  pathParams: zodAssignBOReviewRequestParams,
  responseBody: zodSelfAssignBOReviewRequestResponse,
});

export type SelfAssignBOReviewEndpoint = GetEndpointType<typeof selfAssignBOReviewEndpoint>;

export const zodUnAssignBOReviewRequestResponse = z.object({
  id: ZodId,
});

export const unAssignBOReviewEndpoint = endpoint({
  method: 'DELETE',
  pathParams: zodAssignBOReviewRequestParams,
  responseBody: zodUnAssignBOReviewRequestResponse,
});

export type UnAssignBOReviewEndpoint = GetEndpointType<typeof unAssignBOReviewEndpoint>;

export const ZodPutCashGrantingResponse = ZodReviewedRequest.and(
  z.object({
    type: z.literal('cash_granting'),
    reviewed_at: ZodDateTimeString,
  }),
);
export const ZodGetUnlinkPaymentResponse = ZodReviewedRequest.and(
  z.object({
    type: z.literal('unlink_payment'),
    reviewed_at: ZodDateTimeString,
  }),
);

export const ZodPutUnlinkPaymentResponse = ZodGetUnlinkPaymentResponse;

export const getReviewRequestsEndpoint = endpoint({
  method: 'GET',
  queryParams: ZodGetReviewRequestsQuery,
  responseBody: ZodGetReviewRequestsResponse,
});

export type GetReviewRequestsEndpoint = GetEndpointType<typeof getReviewRequestsEndpoint>;

export const getReviewRequestsIdEndpoint = endpoint({
  method: 'GET',
  pathParams: ZodIDPathParam,
  responseBody: ZodGetReviewRequestResponseItem,
});

export type GetReviewRequestsIdEndpoint = GetEndpointType<typeof getReviewRequestsIdEndpoint>;

export const putReviewRequestsCancelIdEndpoint = endpoint({
  method: 'PUT',
  pathParams: ZodIDPathParam,
  responseBody: ZodPutCancelReviewRequestResponse,
});

export type PutReviewRequestsCancelIdEndpoint = GetEndpointType<
  typeof putReviewRequestsCancelIdEndpoint
>;
